import React from 'react';
import { History, Clock } from 'lucide-react';
import { Badge } from "@/components/ui/badge";

interface StatusHistoryItem {
  id: number;
  old_status: string;
  new_status: string;
  note?: string;
  created_at: Date;
}

interface OrderHistoryTabProps {
  statusHistory: StatusHistoryItem[];
}

export const OrderHistoryTab: React.FC<OrderHistoryTabProps> = ({ statusHistory }) => {
  const getBadgeVariant = (status: string) => {
    switch (status) {
      case "approved":
        return "success";
      case "under_review":
      case "application_submitted":
        return "warning";
      case "pending_payment":
        return "secondary";
      case "denied":
      case "rejected":
        return "destructive";
      default:
        return "outline";
    }
  };
  
  const getStatusText = (status: string) => {
    switch (status) {
      case "approved":
        return "Approved";
      case "under_review":
        return "Under Review";
      case "application_submitted":
        return "Submitted";
      case "pending_payment":
        return "Pending Payment";
      case "denied":
      case "rejected":
        return "Denied";
      default:
        return status.replace(/_/g, " ");
    }
  };

  if (!statusHistory || statusHistory.length === 0) {
    return (
      <div className="text-center py-8">
        <History className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium">No status history</h3>
        <p className="text-sm text-muted-foreground mb-4">
          This order doesn't have any status changes recorded yet.
        </p>
      </div>
    );
  }

  return (
    <ol className="relative border-l border-gray-200 ml-3">
      {statusHistory.map((event, index) => (
        <li key={index} className="mb-6 ml-6">
          <span className="absolute flex items-center justify-center w-6 h-6 bg-white rounded-full -left-3 ring-8 ring-white">
            <Clock className="w-3.5 h-3.5 text-brand-blue" />
          </span>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h3 className="flex items-center text-lg font-medium">
              {getStatusText(event.new_status)}
              <Badge variant={getBadgeVariant(event.new_status)} className="ml-3">
                {getStatusText(event.new_status)}
              </Badge>
            </h3>
            <time className="text-sm text-gray-500">
              {new Date(event.created_at).toLocaleDateString()} at {new Date(event.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </time>
          </div>
          {event.note && (
            <p className="mb-4 text-base font-normal text-gray-500">
              {event.note}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
};
